import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Nav from '../components/app/Nav';
import SEO from '../components/app/SEO';
import FloatingButton from '../components/app/FloatingButton';
import Footer from '../components/app/Footer';
import CalculatorForm, { CalculatorFormData } from '../components/utility/CalculatorForm';
import BoxIllustration from '../components/common/BoxIllustration';
import { ServiceCard } from '../components/common/ServiceCard';
import ServiceDetailModalV2 from '../components/common/ServiceDetailModalV2';
import ExternalServiceCard from '../components/common/ExternalServiceCard';
import AdditionalExternalServiceCard from '../components/common/AdditionalExternalServiceCard';
import {
  RateOption,
  rateService,
  CalculateExternalRatePayload,
  ExternalRateProviderResponse,
  ExternalRateServiceOption,
} from '../services/rate.service';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCheckSquare, faSquare, faCalendarAlt, faHandPointer } from '@fortawesome/free-solid-svg-icons';
import '../styles/pages/CalculatorPage.css';
import additionalStyles from '../styles/common/AdditionalExternalServiceCard.module.css';
import dropoffStoreImg from '../assets/images/dropoff_store.jpg';
import btnStyles from '../styles/components/Button.module.css';

const getTomorrowDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const day = String(tomorrow.getDate()).padStart(2, '0');
  const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
  const year = tomorrow.getFullYear();
  return `${year}-${month}-${day}`;
};

const getMaxDate = () => {
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 7);
  const day = String(maxDate.getDate()).padStart(2, '0');
  const month = String(maxDate.getMonth() + 1).padStart(2, '0');
  const year = maxDate.getFullYear();
  return `${year}-${month}-${day}`;
};

const getCarrierKey = (rate: RateOption) => `${rate.carrierName}-${rate.idCarrierService || 0}`;

const CalculatorPage: React.FC = () => {
  const navigate = useNavigate();
  const [rateResults, setRateResults] = useState<RateOption[]>([]);
  const [showResults, setShowResults] = useState(false);

  // Carrier selection
  const [selectedRate, setSelectedRate] = useState<RateOption | null>(null);
  const [formData, setFormData] = useState<CalculatorFormData | null>(null);

  // External services
  const [externalServices, setExternalServices] = useState<ExternalRateProviderResponse[]>([]);
  const [selectedExternalOption, setSelectedExternalOption] = useState<ExternalRateServiceOption | null>(null);
  const [pickupSelected, setPickupSelected] = useState<boolean>(false);
  const [elpaketDispatcherSelected, setElpaketDispatcherSelected] = useState<boolean>(false);
  const [loadingExternal, setLoadingExternal] = useState<boolean>(false);
  const [pickupDate, setPickupDate] = useState(getTomorrowDate());

  // External indices (for ServiceCard total estimation)
  const [selectedExternalIndicesByCarrier, setSelectedExternalIndicesByCarrier] = useState<Map<string, Set<number>>>(new Map());

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRateForModal, setSelectedRateForModal] = useState<RateOption | null>(null);

  const getCountryName = (code: string) => {
    if (!code) return code;
    try {
      return new Intl.DisplayNames(['es'], { type: 'region' }).of(code.toUpperCase()) || code;
    } catch {
      return code;
    }
  };

  const handleHireService = () => {
    if (!formData || !selectedRate) return;

    navigate('/pickup', {
      state: {
        fromCalculator: true,
        origin: formData.iso2Origin,
        destination: formData.iso2Destination,
        originZip: formData.zipcodeOrigin,
        destinationZip: formData.zipcodeDestination,
        weight: formData.weight,
        width: formData.width,
        height: formData.height,
        length: formData.length,
        fob: formData.fob || 0,
        unitSystem: formData.unitSystem?.toLowerCase() || 'imperial',
        currency: formData.currency || 'USD',
        parcelOrdDoc: formData.parcelOrdDoc || 'PARCEL',
        serviceType: formData.serviceType || 'A',
        hasBattery: formData.hasBattery || false,
        senderAddressLine1: formData.addressLineOrigin,
        senderCity: formData.cityOrigin,
        senderState: formData.stateOrigin,
        receiverAddressLine1: formData.addressLineDestination || '',
        receiverCity: formData.cityDestiny || '',
        receiverState: formData.stateDestiny || '',
        pickupDate: formData.pickupDate,
        selectedService: selectedRate.carrierName,
        idCarrier: selectedRate.idCarrier,
        idCarrierService: selectedRate.idCarrierService,
        pickupSelected: pickupSelected,
        elpaketDispatcherSelected: elpaketDispatcherSelected,
        selectedExternalOption: selectedExternalOption,
      }
    });
  };

  // Dynamic total
  const dynamicTotal = React.useMemo(() => {
    const isNational = formData ? (formData.iso2Origin === formData.iso2Destination) : false;

    if (isNational) {
      const nationalRate = rateResults[0];
      if (!nationalRate) { console.log('[TOTAL] isNational=true but no nationalRate'); return 0; }

      // Carrier service price: find the selected UPS sub-service
      const services = nationalRate.services || [];
      const selectedServiceEntry = services.find(s => s.idCarrierService === selectedRate?.idCarrierService);
      const carrierServicePrice = selectedServiceEntry
        ? selectedServiceEntry.serviceSubTotal
        : (services[0]?.serviceSubTotal ?? 0);

      // Additional: elPaket dispatcher OR pickup
      const elpaketSvc = (externalServices[0] as any)?.elpaketDispatcherService;
      const pickupSvc = (externalServices[0] as any)?.pickUpService;
      const elpaketPrice = (elpaketDispatcherSelected && elpaketSvc) ? (elpaketSvc.serviceSubTotal ?? 0) : 0;
      const pickupPrice = (pickupSelected && !elpaketDispatcherSelected && pickupSvc) ? (pickupSvc.serviceSubTotal ?? 0) : 0;

      return carrierServicePrice + elpaketPrice + pickupPrice;
    }

    // ── International mode ──
    if (!selectedRate) return 0;
    const base = selectedRate.rate || selectedRate.services.reduce((acc, s) => acc + s.serviceSubTotal, 0);
    const externalPrice = (selectedExternalOption && !elpaketDispatcherSelected) ? selectedExternalOption.serviceSubTotal : 0;
    const elpaketPrice = (elpaketDispatcherSelected && externalServices[0]?.elpaketDispatcherService) ? externalServices[0].elpaketDispatcherService.serviceSubTotal : 0;
    const pickupPrice = (pickupSelected && !elpaketDispatcherSelected && externalServices[0]?.pickUpService) ? externalServices[0].pickUpService.serviceSubTotal : 0;
    return base + externalPrice + pickupPrice + elpaketPrice;
  }, [selectedRate, rateResults, selectedExternalOption, pickupSelected, elpaketDispatcherSelected, externalServices, formData]);

  const handleCalculate = (results: RateOption[], data: CalculatorFormData) => {
    const sortedResults = rateService.sortByCheapest(results);
    setRateResults(sortedResults);
    setShowResults(true);
    setFormData(data);
    setPickupDate(data.pickupDate);
    setSelectedRate(null);
    setExternalServices([]);
    setSelectedExternalOption(null);
    setPickupSelected(false);
    setElpaketDispatcherSelected(false);

    if (results.length > 0) {
      const newMap = new Map<string, Set<number>>();
      results.forEach((rate) => {
        newMap.set(getCarrierKey(rate), rateService.getInitialExternalIndices(rate));
      });
      setSelectedExternalIndicesByCarrier(newMap);

      if (data.iso2Origin === data.iso2Destination) {
        const nationalRate = sortedResults[0];
        const sortedServices = [...(nationalRate.services || [])].sort((a, b) => a.serviceSubTotal - b.serviceSubTotal);
        const defaultCarrierService = sortedServices[0]?.idCarrierService || null;
        setSelectedRate({
          ...nationalRate,
          idCarrierService: defaultCarrierService || undefined,
          rate: sortedServices[0]?.serviceSubTotal ?? nationalRate.rate,
        });
        if (nationalRate.externalServices && nationalRate.externalServices.length > 0) {
          setExternalServices(nationalRate.externalServices as any);
          const sortedExtOpts = [...(nationalRate.externalServices[0].options || [])].sort((a, b) => a.serviceSubTotal - b.serviceSubTotal);
          if (sortedExtOpts.length > 0) {
            setSelectedExternalOption(sortedExtOpts[0] as any);
          }
        }
      } else {
        setSelectedRate(sortedResults[0]);
      }
    }

    setTimeout(() => {
      document.getElementById('calculator-results')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleSelectRate = (rate: RateOption) => {
    setSelectedRate(rate);
    setSelectedExternalOption(null);
    setPickupSelected(false);
    setElpaketDispatcherSelected(false);
    setExternalServices([]);
    setTimeout(() => {
      document.getElementById('external-services-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 150);
  };

  // Fetch external rates whenever a carrier is selected
  useEffect(() => {
    const fetchExternalRates = async () => {
      if (!selectedRate || !formData) {
        setExternalServices([]);
        setSelectedExternalOption(null);
        setPickupSelected(false);
        setElpaketDispatcherSelected(false);
        return;
      }
      if (formData.iso2Origin === formData.iso2Destination) {
        // En modo nacional, los servicios adicionales/pickup ya vienen incluidos en la respuesta de calculate-rate
        if (selectedRate.externalServices && selectedRate.externalServices.length > 0) {
          setExternalServices(selectedRate.externalServices as any);
          const firstProvider = selectedRate.externalServices[0];
          if (firstProvider.options?.length > 0 && !selectedExternalOption) {
            setSelectedExternalOption(firstProvider.options[0] as any);
          }
        }
        setLoadingExternal(false);
        return;
      }
      try {
        setLoadingExternal(true);
        const payload: CalculateExternalRatePayload = {
          idCarrier: selectedRate.idCarrier || 1,
          iso2Origin: formData.iso2Origin,
          iso2Destination: formData.iso2Destination,
          height: formData.height,
          width: formData.width,
          length: formData.length,
          weight: formData.weight,
          unitSystem: formData.unitSystem,
          zipcodeOrigin: formData.zipcodeOrigin,
          addressLineOrigin: formData.addressLineOrigin,
          cityOrigin: formData.cityOrigin,
          stateOrigin: formData.stateOrigin,
          pickupDate: pickupDate,
          packages: formData.packages,
        };
        const results = await rateService.calculateExternalRate(payload);
        setExternalServices(results);

        if (results.length > 0 && results[0].options.length > 0) {
          setSelectedExternalOption(results[0].options[0]);
        } else {
          setSelectedExternalOption(null);
        }

        if (results.length > 0 && !results[0].pickUpService) {
          setPickupSelected(false);
        }
        if (results.length > 0 && !results[0].elpaketDispatcherService) {
          setElpaketDispatcherSelected(false);
        }
      } catch (error) {
        console.error('Error fetching external rates:', error);
        setExternalServices([]);
        setSelectedExternalOption(null);
        setPickupSelected(false);
        setElpaketDispatcherSelected(false);
      } finally {
        setLoadingExternal(false);
      }
    };
    fetchExternalRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRate]);

  const handleOpenDetail = (rate: RateOption) => {
    setSelectedRateForModal(rate);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRateForModal(null);
  };

  const selectExternalService = (rate: RateOption, newIndices: Set<number>) => {
    const key = getCarrierKey(rate);
    setSelectedExternalIndicesByCarrier((prev) => {
      const newMap = new Map(prev);
      newMap.set(key, newIndices);
      return newMap;
    });
  };

  const getEstimatedArrivalText = (rate: RateOption) => {
    // Si este es el carrier seleccionado actualmente y hay una opción externa (UPS) elegida:
    const isThisSelected = selectedRate?.carrierName === rate.carrierName && selectedRate?.idCarrierService === rate.idCarrierService;
    const activeExternalOption = isThisSelected ? selectedExternalOption : null;

    if (!activeExternalOption) {
      if (rate.estimatedMinDays && rate.estimatedMaxDays) {
        return `Entrega estimada: ${rate.estimatedMinDays} - ${rate.estimatedMaxDays} días`;
      }
      return "";
    }

    if (!activeExternalOption.transitDays || activeExternalOption.transitDays === "N/A") {
      return "El tiempo de entrega final puede ajustarse a la jornada de los couriers.";
    }

    const extDays = parseInt(activeExternalOption.transitDays) || 0;
    const minDays = rate.estimatedMinDays || 0;
    const maxDays = rate.estimatedMaxDays || 0;

    // Si se seleccionó pickup, se usa pickupDate como inicio y se suma 1 día de recogida.
    // De lo contrario (Drop off), se empieza desde hoy y no se suma día de recogida.
    const start = (pickupSelected || elpaketDispatcherSelected) ? pickupDate : new Date().toISOString().split('T')[0];
    const pickupExtraDay = (pickupSelected || elpaketDispatcherSelected) ? 1 : 0;

    const addBusinessDays = (startDateStr: string, daysToAdd: number): Date => {
      const date = new Date(startDateStr + 'T12:00:00');
      let added = 0;
      while (added < daysToAdd) {
        date.setDate(date.getDate() + 1);
        const day = date.getDay();
        if (day !== 0 && day !== 6) {
          added++;
        }
      }
      return date;
    };

    const formatDateSpanish = (d: Date): string => {
      const months = ['ene.', 'feb.', 'mar.', 'abr.', 'may.', 'jun.', 'jul.', 'ago.', 'sep.', 'oct.', 'nov.', 'dic.'];
      return `${d.getDate()} de ${months[d.getMonth()]}`;
    };

    try {
      const minDate = addBusinessDays(start, extDays + minDays + pickupExtraDay);
      const maxDate = addBusinessDays(start, extDays + maxDays + pickupExtraDay);
      return `Entrega estimada: ${formatDateSpanish(minDate)} - ${formatDateSpanish(maxDate)}`;
    } catch {
      return "El tiempo de entrega final puede ajustarse a la jornada de los couriers.";
    }
  };


  return (
    <>
      <SEO
        title="Calculadora de Tarifas de Envíos | elPaket"
        description="Calcula el costo exacto de tus envíos de paquetes desde cualquier lugar de Estados Unidos a Venezuela, República Dominicana, Costa Rica, Guatemala y Perú."
        canonicalUrl="https://elpaket.com/calculator"
      />
      <Nav />
      <div className="calculator-page-container">
        <div className="calculator-content-wrapper">
          <div className="calculator-hero-wrapper">
            <div className="calculator-hero-content">
              <h1 className="hero-title">
                Calcule sus costos de <br />
                envío al instante
              </h1>
              <p className="hero-subtitle">
                Utilice nuestra Calculadora de envío para obtener una estimación rápida de
                los costos de envío desde cualquier ubicación en USA.
              </p>
              <div className="hero-image-container">
                <BoxIllustration boxStyle={{ maxWidth: '350px' }} />
              </div>
            </div>

            <div className="calculator-form-card-container">
              <CalculatorForm onCalculate={handleCalculate} />
            </div>
          </div>

          {showResults && (
            <div id="calculator-results" className="calculator-results-section">
              <div className="carrierSelectionContainer">
                {formData && formData.iso2Origin === formData.iso2Destination ? (
                  /* Modo Nacional */
                  <>
                    <div className="mainCarriersCol">
                      <h3>
                        Couriers disponibles para
                        <span style={{ display: 'block', fontSize: '1.3em', fontWeight: 800, marginTop: '2px', color: 'var(--brand-black)' }}>
                          {getCountryName(formData?.iso2Destination || '')}
                        </span>
                      </h3>
                      <div className="calc-services-grid">
                        <ServiceCard
                          rateOption={rateResults[0]}
                          isSelected={true}
                          cleanCard={true}
                          estimatedTotal={0}
                          selectedExternalIndices={new Set()}
                          onSelectExternalService={() => {}}
                          onViewDetails={() => {}}
                          onClick={() => {}}
                        />
                      </div>
                    </div>

                    <div className="externalServicesCol">
                        {(() => {
                          const activeProvider = externalServices.find(p => p.providerName?.toLowerCase() === 'ups') || externalServices[0];
                          if (!activeProvider?.elpaketDispatcherService && !activeProvider?.pickUpService) return null;
                          return (
                            <div className={additionalStyles.pickupCardSection} style={{ marginBottom: '20px' }}>
                            <h5 style={{ fontSize: '1.15rem', fontWeight: 700, margin: '0 0 8px', color: 'var(--brand-black)' }}>Drop Off / Pickup</h5>
                            <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 16px' }}>
                              Selecciona cómo nos harás llegar tu paquete.
                            </p>
                            <div className="calc-pickup-grid">
                              {(activeProvider?.providerName?.toLowerCase().includes('ups') || false) && (
                                <div
                                  className={`${additionalStyles.cardContainer} ${(!pickupSelected && !elpaketDispatcherSelected) ? additionalStyles.selected : ''}`}
                                  onClick={() => {
                                    setPickupSelected(false);
                                    setElpaketDispatcherSelected(false);
                                  }}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <div className={additionalStyles.cardMainLayout}>
                                    <div className={additionalStyles.textContent}>
                                      <div className={additionalStyles.topRow}>
                                        <div className={additionalStyles.contentRow}>
                                          <div className={additionalStyles.checkWrapper}>
                                            {(!pickupSelected && !elpaketDispatcherSelected) ? (
                                              <FontAwesomeIcon icon={faCheckSquare} className={additionalStyles.checkIconActive} />
                                            ) : (
                                              <FontAwesomeIcon icon={faSquare} className={additionalStyles.checkIconInactive} />
                                            )}
                                          </div>
                                          <span className={additionalStyles.serviceName}>DROP OFF</span>
                                        </div>
                                        <div className={additionalStyles.freeBadge}>
                                          INCLUIDO
                                        </div>
                                      </div>

                                      <div className={additionalStyles.priceContainer}>
                                        <div className={additionalStyles.priceRow}>
                                          <span className={additionalStyles.priceValue}>$0.00</span>
                                          <span className={additionalStyles.currency}>USD</span>
                                        </div>
                                        <span className={additionalStyles.subtitleText}>
                                          Entregar el paquete en la tienda UPS de su preferencia.
                                        </span>
                                      </div>
                                    </div>

                                    <div className={additionalStyles.illustrationWrapper}>
                                      <img src={dropoffStoreImg} alt="Tienda UPS Drop Off" className={additionalStyles.cardIllustration} />
                                    </div>
                                  </div>
                                </div>
                              )}
                              {activeProvider.elpaketDispatcherService ? (
                                <AdditionalExternalServiceCard
                                  pickupDate={pickupDate}
                                  onDateChange={(e) => setPickupDate(e.target.value)}
                                  minDate={getTomorrowDate()}
                                  maxDate={getMaxDate()}
                                  service={{
                                    serviceName: activeProvider.elpaketDispatcherService.serviceName,
                                    serviceQty: activeProvider.elpaketDispatcherService.serviceQty,
                                    servicePrice: activeProvider.elpaketDispatcherService.servicePrice,
                                    serviceSubTotal: activeProvider.elpaketDispatcherService.serviceSubTotal,
                                    idExternalCourierService: activeProvider.elpaketDispatcherService.idExternalCourierService,
                                    transitDays: activeProvider.elpaketDispatcherService.transitDays || 'N/A',
                                    standardPrice: activeProvider.elpaketDispatcherService.standardPrice,
                                    discountPercent: activeProvider.elpaketDispatcherService.discountPercent,
                                  }}
                                  isSelected={elpaketDispatcherSelected}
                                  onClick={() => {
                                    setElpaketDispatcherSelected(true);
                                    setPickupSelected(false);
                                  }}
                                  currency={selectedRate?.currency || 'USD'}
                                  showDatePicker={true}
                                />
                              ) : activeProvider.pickUpService ? (
                                <AdditionalExternalServiceCard
                                  pickupDate={pickupDate}
                                  onDateChange={(e) => setPickupDate(e.target.value)}
                                  minDate={getTomorrowDate()}
                                  maxDate={getMaxDate()}
                                  service={{
                                    serviceName: activeProvider.pickUpService.serviceName,
                                    serviceQty: activeProvider.pickUpService.serviceQty,
                                    servicePrice: activeProvider.pickUpService.servicePrice,
                                    serviceSubTotal: activeProvider.pickUpService.serviceSubTotal,
                                    idExternalCourierService: activeProvider.pickUpService.idExternalCourierService,
                                    transitDays: activeProvider.pickUpService.transitDays || 'N/A',
                                    standardPrice: activeProvider.pickUpService.standardPrice,
                                    discountPercent: activeProvider.pickUpService.discountPercent,
                                  }}
                                  isSelected={pickupSelected}
                                  onClick={() => {
                                    setPickupSelected(true);
                                    setElpaketDispatcherSelected(false);
                                  }}
                                  currency={selectedRate?.currency || 'USD'}
                                  showDatePicker={true}
                                />
                              ) : null}
                            </div>
                            </div>
                          );
                        })()}

                      <div id="external-services-section" className="calc-external-section">
                        <h3 className="calc-external-title">
                          Traslado Nacional - Servicios disponibles ({rateResults[0]?.carrierName || 'UPS'})
                        </h3>
                        <p className="calc-external-subtitle">
                          Selecciona obligatoriamente un servicio de envío para tu etiqueta.
                        </p>
                        {rateResults[0]?.services && rateResults[0].services.length > 0 && (
                          <div className="calc-external-cards-grid" style={{ marginBottom: '24px' }}>
                            {(() => {
                              const services = [...rateResults[0].services].sort((a, b) => a.serviceSubTotal - b.serviceSubTotal);
                              const minPrice = services[0]?.serviceSubTotal ?? 0;
                              return services.map((option, optIdx) => {
                                let badgeType: 'lowest' | 'recommended' | 'express' | null = null;
                                if (option.transitDays === '1') {
                                  badgeType = 'express';
                                } else if (option.serviceSubTotal === minPrice && services.length > 1) {
                                  badgeType = 'lowest';
                                } else if (option.serviceName.toLowerCase().includes('2nd day') || option.serviceName.toLowerCase().includes('air')) {
                                  badgeType = 'recommended';
                                }
                                const isSelected = selectedRate?.idCarrierService === option.idCarrierService || (!selectedRate?.idCarrierService && optIdx === 0);
                                return (
                                  <ExternalServiceCard
                                    key={optIdx}
                                    service={{
                                      serviceName: option.serviceName,
                                      serviceQty: option.serviceQty,
                                      servicePrice: option.servicePrice,
                                      serviceSubTotal: option.serviceSubTotal,
                                      idExternalCourierService: option.idCarrierService || optIdx,
                                      transitDays: option.transitDays || '4',
                                      estimatedDeliveryDate: option.estimatedDeliveryDate,
                                      standardPrice: option.standardPrice,
                                      discountPercent: option.discountPercent,
                                    }}
                                    isSelected={isSelected}
                                    onClick={() => {
                                      setSelectedRate((prev: any) => ({
                                        ...prev,
                                        idCarrierService: option.idCarrierService,
                                        rate: option.serviceSubTotal
                                      }));
                                    }}
                                    currency={rateResults[0].currency || 'USD'}
                                    badgeType={badgeType}
                                  />
                                );
                              });
                            })()}
                          </div>
                        )}

                        <div className="calc-dynamic-total">
                          <span className="calc-dynamic-total-label">Total estimado</span>
                          <span className="calc-dynamic-total-value">
                            {dynamicTotal.toFixed(2)} {selectedRate?.currency || 'USD'}
                          </span>
                        </div>
                        <button
                          className={`${btnStyles.primary} ${btnStyles.fullWidth}`}
                          style={{ marginTop: '20px' }}
                          onClick={handleHireService}
                        >
                          SOLICITAR RECOGIDA / CONTINUAR
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  /* Modo Internacional */
                  <>
                    <div className="mainCarriersCol">
                      <h3>
                        Couriers disponibles para
                        <span style={{ display: 'block', fontSize: '1.3em', fontWeight: 800, marginTop: '2px', color: 'var(--brand-black)' }}>
                          {getCountryName(formData?.iso2Destination || '')}
                        </span>
                      </h3>
                      {rateResults.length > 1 && (
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          background: 'rgba(255, 193, 20, 0.08)',
                          border: '1.5px dashed var(--elPaket-primary-color, #ffc114)',
                          borderRadius: '10px',
                          padding: '8px 14px',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: 'var(--brand-black)',
                          margin: '4px 0 16px'
                        }}>
                          <FontAwesomeIcon icon={faHandPointer} style={{ color: 'var(--elPaket-primary-color, #ffc114)' }} />
                          Selecciona el Courier de tu preferencia
                        </div>
                      )}
                      <div className="calc-services-grid">
                        {rateResults.map((rate, idx) => {
                          const key = getCarrierKey(rate);
                          const isSelected = selectedRate?.carrierName === rate.carrierName && selectedRate?.idCarrierService === rate.idCarrierService;
                          const estimatedTotal = rateService.calculateTotal(
                            rate,
                            selectedExternalIndicesByCarrier.get(key) || rateService.getInitialExternalIndices(rate)
                          );
                          return (
                            <ServiceCard
                              key={idx}
                              rateOption={rate}
                              estimatedTotal={estimatedTotal}
                              isSelected={isSelected}
                              onClick={() => handleSelectRate(rate)}
                              selectedExternalIndices={selectedExternalIndicesByCarrier.get(key) || new Set()}
                              onSelectExternalService={(newIndices) => selectExternalService(rate, newIndices)}
                              isCurrentSelectedService={false}
                              onViewDetails={() => handleOpenDetail(rate)}
                              estimatedArrivalText={getEstimatedArrivalText(rate)}
                            />
                          );
                        })}
                      </div>
                    </div>

                    {selectedRate && (
                      <div className="externalServicesCol">
                        {!loadingExternal && externalServices.length > 0 && (() => {
                                const activeProvider = externalServices.find(p => p.providerName?.toLowerCase() === 'ups') || externalServices[0];
                                if (!activeProvider?.elpaketDispatcherService && !activeProvider?.pickUpService) return null;
                                return (
                                  <div className={additionalStyles.pickupCardSection} style={{ marginBottom: '20px' }}>
                                  <h5 style={{ fontSize: '1.15rem', fontWeight: 700, margin: '0 0 8px', color: 'var(--brand-black)' }}>Drop Off / Pickup</h5>
                                  <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 16px' }}>
                                    Selecciona cómo nos harás llegar tu paquete.
                                  </p>
                                  <div className="calc-pickup-grid">
                                    {(activeProvider?.providerName?.toLowerCase().includes('ups') || false) && (
                                      <div
                                        className={`${additionalStyles.cardContainer} ${(!pickupSelected && !elpaketDispatcherSelected) ? additionalStyles.selected : ''}`}
                                        onClick={() => {
                                          setPickupSelected(false);
                                          setElpaketDispatcherSelected(false);
                                        }}
                                        style={{ cursor: 'pointer' }}
                                      >
                                          <div className={additionalStyles.cardMainLayout}>
                                            <div className={additionalStyles.textContent}>
                                              <div className={additionalStyles.topRow}>
                                                <div className={additionalStyles.contentRow}>
                                                  <div className={additionalStyles.checkWrapper}>
                                                    {(!pickupSelected && !elpaketDispatcherSelected) ? (
                                                      <FontAwesomeIcon icon={faCheckSquare} className={additionalStyles.checkIconActive} />
                                                    ) : (
                                                      <FontAwesomeIcon icon={faSquare} className={additionalStyles.checkIconInactive} />
                                                    )}
                                                  </div>
                                                  <span className={additionalStyles.serviceName}>DROP OFF</span>
                                                </div>
                                                <div className={additionalStyles.freeBadge}>
                                                  INCLUIDO
                                                </div>
                                              </div>

                                              <div className={additionalStyles.priceContainer}>
                                                <div className={additionalStyles.priceRow}>
                                                  <span className={additionalStyles.priceValue}>$0.00</span>
                                                  <span className={additionalStyles.currency}>USD</span>
                                                </div>
                                                <span className={additionalStyles.subtitleText}>
                                                  Entregar el paquete en la tienda UPS de su preferencia.
                                                </span>
                                              </div>
                                            </div>

                                            <div className={additionalStyles.illustrationWrapper}>
                                              <img src={dropoffStoreImg} alt="Tienda UPS Drop Off" className={additionalStyles.cardIllustration} />
                                            </div>
                                          </div>
                                        </div>
                                    )}
                                    {activeProvider.elpaketDispatcherService ? (
                                      <AdditionalExternalServiceCard
                                        pickupDate={pickupDate}
                                        onDateChange={(e) => setPickupDate(e.target.value)}
                                        minDate={getTomorrowDate()}
                                        maxDate={getMaxDate()}
                                        service={{
                                          serviceName: activeProvider.elpaketDispatcherService.serviceName,
                                          serviceQty: activeProvider.elpaketDispatcherService.serviceQty,
                                          servicePrice: activeProvider.elpaketDispatcherService.servicePrice,
                                          serviceSubTotal: activeProvider.elpaketDispatcherService.serviceSubTotal,
                                          idExternalCourierService: activeProvider.elpaketDispatcherService.idExternalCourierService,
                                          transitDays: activeProvider.elpaketDispatcherService.transitDays || 'N/A',
                                          standardPrice: activeProvider.elpaketDispatcherService.standardPrice,
                                          discountPercent: activeProvider.elpaketDispatcherService.discountPercent,
                                        }}
                                        isSelected={elpaketDispatcherSelected}
                                        onClick={() => {
                                          setElpaketDispatcherSelected(true);
                                          setPickupSelected(false);
                                        }}
                                        currency={selectedRate?.currency || 'USD'}
                                        showDatePicker={true}
                                      />
                                    ) : activeProvider.pickUpService ? (
                                      <AdditionalExternalServiceCard
                                        pickupDate={pickupDate}
                                        onDateChange={(e) => setPickupDate(e.target.value)}
                                        minDate={getTomorrowDate()}
                                        maxDate={getMaxDate()}
                                        service={{
                                          serviceName: activeProvider.pickUpService.serviceName,
                                          serviceQty: activeProvider.pickUpService.serviceQty,
                                          servicePrice: activeProvider.pickUpService.servicePrice,
                                          serviceSubTotal: activeProvider.pickUpService.serviceSubTotal,
                                          idExternalCourierService: activeProvider.pickUpService.idExternalCourierService,
                                          transitDays: activeProvider.pickUpService.transitDays || 'N/A',
                                          standardPrice: activeProvider.pickUpService.standardPrice,
                                          discountPercent: activeProvider.pickUpService.discountPercent,
                                        }}
                                        isSelected={pickupSelected}
                                        onClick={() => {
                                          setPickupSelected(true);
                                          setElpaketDispatcherSelected(false);
                                        }}
                                        currency={selectedRate?.currency || 'USD'}
                                        showDatePicker={true}
                                      />
                                    ) : null}
                                  </div>
                                  </div>
                                );
                              })()}

                        <div id="external-services-section" className="calc-external-section">
                          <h3 className="calc-external-title">
                            Traslado Nacional - Servicios disponibles ({externalServices[0]?.providerName || 'UPS'})
                          </h3>
                          <p className="calc-external-subtitle">
                            Selecciona el servicio de envío para tu etiqueta con el courier seleccionado.
                          </p>

                          {loadingExternal ? (
                            <div className="calc-external-loading">
                              <FontAwesomeIcon icon={faSpinner} spin style={{ color: 'var(--elPaket-primary-color)', fontSize: '1.5rem' }} />
                              <span>Cargando servicios disponibles...</span>
                            </div>
                          ) : externalServices.length > 0 ? (
                            <>
                              <div className="calc-external-cards-grid" style={elpaketDispatcherSelected ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                                {(() => {
                                  const allOptions = externalServices.flatMap(p => p.options || []);
                                  const minPrice = allOptions.length > 0 ? Math.min(...allOptions.map(o => o.serviceSubTotal)) : 0;
                                  return externalServices.flatMap(provider => {
                                    const sortedOpts = [...(provider.options || [])].sort((a, b) => a.serviceSubTotal - b.serviceSubTotal);
                                    return sortedOpts.map((option, optIdx) => {
                                      let badgeType: 'lowest' | 'recommended' | 'express' | null = null;
                                      if (option.transitDays === '1') {
                                        badgeType = 'express';
                                      } else if (option.serviceSubTotal === minPrice && allOptions.length > 1) {
                                        badgeType = 'lowest';
                                      } else if (option.serviceName.toLowerCase().includes('2nd day') || option.serviceName.toLowerCase().includes('air')) {
                                        badgeType = 'recommended';
                                      }
                                      return (
                                        <ExternalServiceCard
                                          key={optIdx}
                                          service={{
                                            serviceName: option.serviceName,
                                            serviceQty: option.serviceQty,
                                            servicePrice: option.servicePrice,
                                            serviceSubTotal: option.serviceSubTotal,
                                            idExternalCourierService: option.idExternalCourierService,
                                            transitDays: option.transitDays || 'N/A',
                                            estimatedDeliveryDate: option.estimatedDeliveryDate,
                                            standardPrice: option.standardPrice,
                                            discountPercent: option.discountPercent,
                                          }}
                                          isSelected={!elpaketDispatcherSelected && selectedExternalOption?.idExternalCourierService === option.idExternalCourierService}
                                          onClick={() => {
                                            setSelectedExternalOption(option);
                                            setElpaketDispatcherSelected(false);
                                          }}
                                          currency={selectedRate?.currency || 'USD'}
                                          badgeType={badgeType}
                                        />
                                      );
                                    });
                                  });
                                })()}
                              </div>

                              {/* Dynamic Total */}
                              <div className="calc-dynamic-total">
                                <span className="calc-dynamic-total-label">Total estimado</span>
                                <span className="calc-dynamic-total-value">
                                  {dynamicTotal.toFixed(2)} {selectedRate?.currency || 'USD'}
                                </span>
                              </div>
                              <button
                                className={`${btnStyles.primary} ${btnStyles.fullWidth}`}
                                style={{ marginTop: '20px' }}
                                onClick={handleHireService}
                              >
                                SOLICITAR RECOGIDA / CONTINUAR
                              </button>
                            </>
                          ) : (
                            <p className="calc-no-external">No hay servicios externos disponibles para este courier.</p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <ServiceDetailModalV2
        show={isModalOpen}
        onHide={handleCloseModal}
        rateOption={selectedRateForModal}
        selectedExternalIndices={selectedRateForModal ? selectedExternalIndicesByCarrier.get(getCarrierKey(selectedRateForModal)) || new Set() : new Set()}
        onSelectExternalService={(newIndices) => {
          if (selectedRateForModal) {
            selectExternalService(selectedRateForModal, newIndices);
          }
        }}
      />
      {/*
      <FloatingButton />
      */}
      <Footer />
    </>
  );
};

export default CalculatorPage;

